import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { Toaster } from "./components/ui/sonner";
import { AuthProvider, useAuth } from "./context/AuthContext";

// Pages
import SplashPage from "./pages/SplashPage";
import AcknowledgementPage from "./pages/AcknowledgementPage";
import LoginPage from "./pages/LoginPage";
import DashboardPage from "./pages/DashboardPage";
import ProjectsPage from "./pages/ProjectsPage";
import ToolsPage from "./pages/ToolsPage";
import DeliveryPage from "./pages/DeliveryPage";
import DMLBuilderPage from "./pages/DMLBuilderPage";
import BREXValidationPage from "./pages/BREXValidationPage";
import BREXBuilderPage from "./pages/BREXBuilderPage";
import AuthorPage from "./pages/AuthorPage";
import PDFPublishPage from "./pages/PDFPublishPage";
import STECheckPage from "./pages/STECheckPage";
import SecurityPage from "./pages/SecurityPage";
import SettingsPage from "./pages/SettingsPage";
import ChangePasswordPage from "./pages/ChangePasswordPage";
import ResourcesPage from "./pages/ResourcesPage";
import HistoryPage from "./pages/HistoryPage";
import CustomerHistoryPage from "./pages/CustomerHistoryPage";
import UsersPage from "./pages/UsersPage";
import TeamPage from "./pages/TeamPage";
import ProductConfigPage from "./pages/ProductConfigPage";
import ConversionPage from "./pages/ConversionPage";

import ProjectDashboardPage from "./pages/ProjectDashboardPage";
import ProjectEstimatorPage from "./pages/estimator/ProjectEstimatorPage";
import { ToolGuard } from "./components/ToolGuard";

// Layout
import DashboardLayout from "./layouts/DashboardLayout";

// Components
import ProtectedRoute from "./components/ProtectedRoute";

// Auth redirect component
const AuthRedirect = ({ children }) => {
  const { isAuthenticated, loading } = useAuth();
  
  if (loading) return null;
  if (isAuthenticated) return <Navigate to="/dashboard" replace />;
  return children;
};

// Role-based history: admins get global history, primary customers get company history
const HistoryRouter = () => {
  const { user } = useAuth();
  return user?.role === 'admin' ? <HistoryPage /> : <CustomerHistoryPage />;
};

// Deep link prevention: on initial page load, always land on the Dashboard
let deepLinkChecked = false;
const DeepLinkGuard = () => {
  const navigate = useNavigate();
  React.useEffect(() => {
    if (deepLinkChecked) return;
    deepLinkChecked = true;
    const p = window.location.pathname;
    if (p.startsWith('/dashboard') && p !== '/dashboard' && p !== '/dashboard/') {
      navigate('/dashboard', { replace: true });
    }
  }, [navigate]);
  return null;
};

function AppRoutes() {
  return (
    <>
    <DeepLinkGuard />
    <Routes>
      {/* Public Routes */}
      <Route 
        path="/" 
        element={
          <AuthRedirect>
            <SplashPage />
          </AuthRedirect>
        } 
      />
      <Route 
        path="/login" 
        element={
          <AuthRedirect>
            <LoginPage />
          </AuthRedirect>
        } 
      />
      <Route 
        path="/acknowledgement" 
        element={
          <AuthRedirect>
            <AcknowledgementPage />
          </AuthRedirect>
        } 
      />
      <Route 
        path="/change-password" 
        element={
          <ProtectedRoute>
            <ChangePasswordPage />
          </ProtectedRoute>
        } 
      />

      {/* Protected Routes */}
      <Route
        path="/dashboard"
        element={
          <ProtectedRoute>
            <DashboardLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="estimator" element={<ToolGuard toolKey="project-estimator"><ProjectEstimatorPage /></ToolGuard>} />
        <Route path="projects" element={<ProjectsPage />} />
        <Route path="projects/:projectId" element={<ProjectDashboardPage />} />
        <Route path="tools" element={<ToolsPage />} />
        <Route path="tools/delivery" element={<ToolGuard toolKey="delivery"><DeliveryPage /></ToolGuard>} />
        <Route path="tools/dml-builder" element={<ToolGuard toolKey="dml-builder"><DMLBuilderPage /></ToolGuard>} />
        <Route path="tools/brex-validation" element={<ToolGuard toolKey="brex-validation"><BREXValidationPage /></ToolGuard>} />
        <Route path="tools/brex-builder" element={<ToolGuard toolKey="brex-builder"><BREXBuilderPage /></ToolGuard>} />
        <Route path="tools/author" element={<ToolGuard toolKey="author"><AuthorPage /></ToolGuard>} />
        <Route path="tools/pdf-publish" element={<ToolGuard toolKey="pdf-publish"><PDFPublishPage /></ToolGuard>} />
        <Route path="tools/ste-check" element={<ToolGuard toolKey="ste-check"><STECheckPage /></ToolGuard>} />
        <Route path="tools/conversion" element={<ToolGuard toolKey="conversion"><ConversionPage /></ToolGuard>} />
        <Route path="resources" element={<ResourcesPage />} />
        <Route path="history" element={<HistoryRouter />} />
        <Route path="users" element={<UsersPage />} />
        <Route path="product-config" element={<ProductConfigPage />} />
        <Route path="team" element={<TeamPage />} />
        <Route path="security" element={<SecurityPage />} />
        <Route path="settings" element={<SettingsPage />} />
      </Route>

      {/* Catch all - redirect to splash */}
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
    </>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <div className="App">
          <AppRoutes />
          <Toaster position="top-right" richColors closeButton />
        </div>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
